import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ExperienceInfo from "../../components/UserComponents/ExperienceInfo";
import VendorInfo from "../../components/UserComponents/VendorInfo";
import CustomButton from "../../components/common/CustomButton";
import {
  useBookingCalendar,
  useVendorBookingById,
  useVendorBookingComplete,
  useVendorBookingConfirm,
  useVendorBookingDeny,
  useVendorSuggestAlternateTime,
} from "../../custom-hooks/queries";
import { downloadICSFile, formatDate } from "../../utils/HelperMethod";
import { toast } from "react-toastify";

import {
  // FiDownload,
  FiMessageSquare,
  FiStar,
  FiCheck,
  FiClock,
  FiUser,
  FiCalendar,
} from "react-icons/fi";
import SuggestAlternateTimeModal from "../../components/vendors/SuggestAlternateTime";
import CustomerInfoCard from "../../components/vendors/CustonerInfoCard";
import BookingStatusTimeline from "../../components/common/BookingStatusTimeline";
import StatusBadge from "../../components/vendors/StatusBadge";
import { SHOW_BUTTON_STATUSES } from "../../utils/constants";

/* ================= SKELETON ================= */
const BookingDetailSkeleton = () => (
  <div className="w-full max-w-[1380px] mx-auto px-6 py-10 animate-pulse">
    <div className="h-8 w-64 bg-gray-200 rounded mb-6" />
    <div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-10">
      <div>
        <div className="w-full h-[320px] bg-gray-200 rounded-xl" />
        <div className="grid grid-cols-4 gap-4 mt-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-[100px] bg-gray-200 rounded-xl" />
          ))}
        </div>
      </div>
      <div className="border rounded-2xl p-6">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-4 bg-gray-200 rounded mb-3" />
        ))}
      </div>
    </div>
  </div>
);

/* ================= PAGE ================= */
export default function VendorBookingDetailPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, isError, refetch } = useVendorBookingById(bookingId);

  const booking = data?.data || {};
  const experience = booking?.experience || {};
  const vendor = booking?.customer || {};
  const statusTimeline = booking?.statusTimeline || {};
  const currentStatus = booking?.currentStatus || "";
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const { mutate: completeBooking, isPending: isCompleting } =
    useVendorBookingComplete();

  const isBookingConfirmed = !!statusTimeline.bookingConfirmed;
  const isUpcoming = !!statusTimeline.upcoming;
  const isAwaitingConfirmation = !!statusTimeline.awaitingUserConfirmation;
  const isCompleted = !!statusTimeline.completed;

  const { mutate: acceptBooking, isLoading: isAccepting } =
    useVendorBookingConfirm();
  const { mutate: denyBooking, isLoading: isDenying } = useVendorBookingDeny();
  const { mutate: suggestAlternateTime } = useVendorSuggestAlternateTime();

  const {
    mutate: calendarData,
    isPending: isCalendarLoading,
    isError: isCalendarError,
  } = useBookingCalendar(bookingId);

  const normalizedStatus = booking?.currentStatus?.toLowerCase()?.trim();

  // const handleDownloadReceipt = () => {
  //   if (booking?.receiptDownloadUrl) {
  //     window.open(booking.receiptDownloadUrl, "_blank");
  //   }
  // };

  if (isLoading) return <BookingDetailSkeleton />;

  if (isError || !booking?.bookingId) {
    return (
      <div className="text-center py-20">
        <h2 className="text-2xl font-semibold">Booking not found</h2>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1380px] mx-auto px-6 py-10">
      {/* PAGE TITLE */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-[28px] font-semibold text-gray-900 mb-6">
          {experience.title || "Booking Details"}
        </h1>
        {booking?.currentStatus === "Pending Vendor Confirmation" && (
          <div className="flex gap-2">
            <CustomButton
              variant="primary"
              onClick={() =>
                acceptBooking(
                  { bookingId: bookingId },
                  {
                    onSuccess: () => {
                      // Optionally, you can show a success message or refresh data here
                      toast.success("Booking accepted successfully");
                      refetch();
                    },
                    onError: (err) => {
                      toast.error(
                        err?.response?.data?.error_message ||
                          "Failed to accept booking",
                      );
                    },
                  },
                )
              }
            >
              Accept
            </CustomButton>

            <CustomButton
              variant="secondary"
              onClick={() =>
                denyBooking(
                  { bookingId: bookingId },
                  {
                    onSuccess: () => {
                      toast.success("Booking rejected successfully");
                      refetch();
                    },
                    onError: (err) => {
                      toast.error(
                        err?.response?.data?.error_message ||
                          "Failed to reject booking",
                      );
                    },
                  },
                )
              }
            >
              Reject
            </CustomButton>

            {/* <CustomButton
              variant="secondary"
              onClick={() => setShowSuggestModal(true)}
            >
              Suggest Alternate Time
            </CustomButton> */}
          </div>
        )}
        {SHOW_BUTTON_STATUSES.includes(normalizedStatus) && (
          <CustomButton
            onClick={() =>
              calendarData(bookingId, {
                onSuccess: (icsData) => {
                  downloadICSFile(icsData, `booking-${bookingId}.ics`);
                  toast.success("Calendar file downloaded");
                },
                onError: (error) => {
                  toast.error(
                    error?.response?.data?.message ||
                      "Failed to complete the action.",
                  );
                },
              })
            }
            variant="primary"
            loading={isCalendarLoading}
          >
            Add to Calendar
          </CustomButton>
        )}
        {booking?.currentStatus.toLowerCase() === "ongoing" && (
          <CustomButton
            variant="primary"
            onClick={() =>
              completeBooking(
                { bookingId },
                {
                  onSuccess: () => {
                    toast.success("Successfully completed the booking.");
                    refetch();
                  },
                  onError: (error) => {
                    toast.error(
                      error?.response?.data?.message ||
                        "Failed to complete the booking.",
                    );
                  },
                },
              )
            }
            loading={isCompleting}
          >
            Complete
          </CustomButton>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-10">
        {/* ================= LEFT ================= */}
        <div>
          {/* HERO IMAGE */}
          {experience.images?.[0] && (
            <div className="w-full h-[320px] overflow-hidden rounded-xl">
              <img
                src={experience.images[0]}
                alt={experience.title}
                className="w-full h-full object-cover object-center"
                loading="lazy"
              />
            </div>
          )}

          {/* GALLERY */}
          {experience.images?.length > 1 && (
            <div className="grid grid-cols-4 gap-4 mt-4">
              {experience.images.slice(1, 5).map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  className="h-[100px] w-full object-cover rounded-xl"
                  alt={`Gallery ${idx + 1}`}
                  loading="lazy"
                />
              ))}
            </div>
          )}

          {/* EXPERIENCE INFO */}
          <div className="mt-10">
            <ExperienceInfo
              title={experience.title}
              rating={experience.rating}
              reviewsCount={experience.totalReviews}
              location={experience.location}
              price={experience?.price}
              duration={experience.duration}
              category={experience.category}
              description={experience.description}
              priceType={experience.priceType}
            />
          </div>

          {/* VENDOR INFO */}
          {vendor?.name && (
            <div className="mt-10">
              <CustomerInfoCard
                name={vendor.name}
                email={vendor.email}
                phone={vendor.phone}
              />
            </div>
          )}
        </div>

        {/* ================= RIGHT ================= */}

        <div className="border border-gray-200 rounded-2xl p-6 shadow-sm h-fit">
          <h2 className="text-lg font-semibold mb-5">Booking Summary</h2>

          <SummaryRow label="Booking ID" value={booking.bookingCode} />
          <SummaryRow
            label="Booking Date"
            value={formatDate(booking.bookingDate)}
          />
          <SummaryRow
            label="Time Slot"
            value={`${booking?.timeSlot || "N/A"}`}
          />
          <SummaryRow
            label="Number of People"
            value={`${booking?.numberOfPeople || 0}`}
          />
          <SummaryRow label="Payment Method" value={booking.paymentMethod} />

          <div className="flex justify-between items-center mt-4">
            <span className="font-medium text-gray-800">Payment Status</span>

            <StatusBadge status={booking.paymentStatus.toLowerCase()} />
          </div>

          <hr className="my-6" />

          {/* STATUS */}
          <BookingStatusTimeline
            title={"Booking Status"}
            timeline={booking?.vendorBookingstatusTimeline || []}
          />

          {booking?.experience?.importantNotes && (
            <>
              <hr className="my-6" />
              <h2 className="text-lg font-semibold mb-4">Notes</h2>
              <p className="text-sm text-gray-700">
                • {booking?.experience.importantNotes}
              </p>
            </>
          )}

          {currentStatus === "AwaitingUserConfirmation" && (
            <div className="mt-6">
              <CustomButton className="w-full">
                Mark Experience as Completed
              </CustomButton>
            </div>
          )}

          <hr className="my-6" />

          <div className="flex flex-col gap-3">
            {/* <ActionButton
              icon={<FiDownload size={18} />}
              label="Download Receipt"
              onClick={handleDownloadReceipt}
            /> */}
            <ActionButton
              icon={<FiMessageSquare size={18} />}
              label="Contact Support"
              onClick={() => navigate("/vendor/support")}
            />
            {isCompleted && (
              <ActionButton icon={<FiStar size={18} />} label="View Review" />
            )}
          </div>
        </div>
      </div>
      <SuggestAlternateTimeModal
        isOpen={showSuggestModal}
        onClose={() => setShowSuggestModal(false)}
        onSubmit={(data) => {
          setShowSuggestModal(false);
        }}
        refetch={refetch}
        mode="edit"
        bookingId={bookingId}
      />
    </div>
  );
}

/* ================= SMALL COMPONENTS ================= */

function SummaryRow({ label, value }) {
  return (
    <div className="flex justify-between text-sm py-1">
      <span className="text-gray-700">{label}</span>
      <span className="font-semibold text-gray-900">{value}</span>
    </div>
  );
}

function StatusItem({ icon, label, active, completed }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div
        className={`h-9 w-9 rounded-full flex items-center justify-center ${
          completed
            ? "bg-[#21C55D] text-white"
            : active
              ? "bg-[#00C3CC] text-white"
              : "bg-gray-300 text-white"
        }`}
      >
        {icon}
      </div>
      <span
        className={`text-sm ${
          active || completed ? "text-[#00C3CC]" : "text-gray-500"
        }`}
      >
        {label}
      </span>
    </div>
  );
}

function ActionButton({ icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full border border-gray-300 rounded-xl py-3 flex items-center gap-3 px-4 hover:bg-gray-100 transition"
    >
      {icon}
      <span className="text-gray-800 text-sm font-medium">{label}</span>
    </button>
  );
}
